const KEY = "headcount.next";
/** How long a page remembered before Google sign-in stays good; a user who backs out of Google never comes back for it. */
const TTL_MS = 10 * 60_000;

/**
 * A path to go to after signing in, from a `next` query value: only a same-origin path such as `/careers/x/apply`, never
 * `//host`, `/\host`, a scheme, or anything with control characters, and never a path that only becomes `//host` once its
 * dot segments resolve, such as `/..//host`. Null otherwise.
 */
export function safeNext(raw: string | null | undefined): string | null {
  if (!raw || raw.length > 200 || !raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\") || /[\u0000-\u001f\u007f\\]/.test(raw)) return null;
  try {
    const base = "https://headcount.invalid";
    const u = new URL(raw, base);
    return u.origin === base && !u.pathname.startsWith("//") ? u.pathname + u.search : null;
  } catch {
    return null;
  }
}

/** The `next` of a query string, when it is safe. */
export const nextOf = (search: string) => safeNext(new URLSearchParams(search).get("next"));

/**
 * Remembers where to go for the trip through Google, which returns to the site root: the tab's sessionStorage survives it,
 * so no extra Supabase redirect URL is needed.
 */
export function rememberNext(path: string | null): void {
  try {
    if (path) sessionStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
    else sessionStorage.removeItem(KEY);
  } catch {}
}

/** The remembered path, once: reading it forgets it. */
export function takeNext(): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    const v = raw ? (JSON.parse(raw) as { path?: string; at?: number }) : null;
    return v && Date.now() - (v.at ?? 0) < TTL_MS ? safeNext(v.path) : null;
  } catch {
    return null;
  }
}
